import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { ApiService } from 'src/app/shared/services/api.service';
import { LearningQuizFormValue } from './learning-quiz-form/learning-quiz-form.component';

interface ParsedAnswer {
  answerText: string;
  matchingKey?: string;
  markdown?: string;
}
interface ModalDialogRef {
  openModalDialog(): void;
}

interface UserAnswer {
  questionIndex: number;
  selectedAnswers: number[];
  isCorrect?: boolean;
  matchedPairs?: Record<string, string>;
}

// API Question Interface
export interface ApiQuizQuestion {
  _path: string;
  questionType: string;
  questionText: string;
  answers_terms: string[];
  answers?: ParsedAnswer[]; // For parsed answers
  definitions: string[] | null;
  explanation: {
    html: string;
  };
}

// API Get Quiz Answer Interface
export interface ApiGetQuizAnswer {
  correct: boolean[];
  explanation: {
    html: string;
  };
  numberOfCorrectAnswers: number;
}

// Matching question pairs
interface MatchingPair {
  term: string;
  definition: string;
  selectedDefinitionIndex: number | null;
}

interface MatchingTerm {
  text: string;
  index: number;
  key: string;
}

interface MatchingDefinition {
  text: string;
  correctTermIndex: number;
  key: string;
  matchedTermKey?: string;
}

@Component({
  selector: 'app-learning-quiz-questions',
  templateUrl: './learning-quiz-questions.component.html',
  styleUrls: ['./learning-quiz-questions.component.scss'],
  standalone: false,
})
export class LearningQuizQuestionsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() lessonId!: string;
  @Input() lessonTitle?: string;
  @Input() quizQuestions: ApiQuizQuestion[] = []; // API quiz questions ONLY
  @Input() quizPath?: string; // Path to fetch quiz data from API
  @Input() quizData?: any; // Full quiz data including title, path, etc.
  @Input() lessonPath?: string; // AEM content fragment path of the lesson the quiz belongs to
  @Input() quizFormValue?: LearningQuizFormValue; // Values submitted from the quiz intro form
  @Output() backToQuizIntro = new EventEmitter<void>();
  @Output() backToLibrary = new EventEmitter<void>();
  @Output() submitQuiz = new EventEmitter<any>();

  @ViewChild('backWarningDialog') backWarningDialog: ModalDialogRef | undefined;
  @ViewChild('quizTop', { read: ElementRef }) quizTop:
    | ElementRef<HTMLElement>
    | undefined;
  @ViewChild('backToLibraryBtn', { read: ElementRef }) backToLibraryBtn:
    | ElementRef<HTMLElement>
    | undefined;
  @ViewChild('continueBtn', { read: ElementRef })
  continueBtnRef?: ElementRef<HTMLElement>;

  private _initFocusTimer?: ReturnType<typeof setTimeout>;
  private _checkFocusTimer?: ReturnType<typeof setTimeout>;
  private _matchingAnnouncementTimer?: ReturnType<typeof setTimeout>;

  continueAriaLabel: string = '';

  focusedTermIndex: number = 0;
  focusedDefinitionIndex: number = 0;
  termsListFocused: boolean = false;
  definitionsListFocused: boolean = false;
  matchingAnnouncement: string = '';

  currentQuestionIndex: number = 0;
  userAnswers: UserAnswer[] = [];
  showFeedback: boolean = false;
  currentFeedback: { correct: boolean; explanation: string } | null = null;
  currentQuizAnswerResponse: ApiGetQuizAnswer | null = null;
  isXsScreen: boolean = false;
  isTabletOrBelow: boolean = false;
  isLoadingQuizAnswer: boolean = false;
  isLoadingQuestions: boolean = false;
  isSavingScore: boolean = false;
  draggedTerm: MatchingTerm | null = null;
  selectedTermForClick: MatchingTerm | null = null;
  matchingTerms: MatchingTerm[] = [];
  matchingDefinitions: MatchingDefinition[] = [];
  isDraggingFromDefinition: boolean = false;
  captchaResponse: string | null = null;
  private readonly _destroyed$ = new Subject<void>();
  private readonly _shuffledAnswersCache = new Map<number, ParsedAnswer[]>();

  // Additional properties needed
  questions: ApiQuizQuestion[] = [];
  matchingPairs: MatchingPair[] = [];
  availableDefinitions: MatchingDefinition[] = [];
  cachedAnswers: ParsedAnswer[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private _translate: TranslateService,
    private readonly _apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.checkScreenSize();
    window.addEventListener('resize', this.onResize);
    this.loadQuestions();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  ngAfterViewInit(): void {
    this._initFocusTimer = setTimeout(() =>
      this.backToLibraryBtn?.nativeElement?.focus()
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this._destroyed$.next();
    this._destroyed$.complete();
    clearTimeout(this._initFocusTimer);
    clearTimeout(this._checkFocusTimer);
    clearTimeout(this._matchingAnnouncementTimer);
  }

  private onResize = () => {
    this.checkScreenSize();
  };

  private checkScreenSize(): void {
    this.isXsScreen = window.innerWidth < 576;
    this.isTabletOrBelow = window.innerWidth <= 1024;
  }

  get isLoading(): boolean {
    return (
      this.isLoadingQuestions || this.isLoadingQuizAnswer || this.isSavingScore
    );
  }

  get loadingStatusKey(): string {
    if (this.isLoadingQuestions) {
      return 'studentLearningQuiz.loading_questions';
    }
    return this.isSavingScore
      ? 'studentLearningQuiz.saving_score'
      : 'studentLearningQuiz.checking_answer';
  }

  loadQuestions(): void {
    if (this.quizPath) {
      this.isLoadingQuestions = true;
      this._apiService
        .getLessonQuiz(this.quizPath)
        .pipe(takeUntil(this._destroyed$))
        .subscribe({
          next: (response) => {
            const quizData =
              Array.isArray(response) && response.length > 0
                ? response[0]
                : response;

            if (quizData && quizData.questionPath) {
              this.questions = quizData.questionPath;
              this.quizQuestions = quizData.questionPath;

              this.userAnswers = this.questions.map((_, index) => ({
                questionIndex: index,
                selectedAnswers: [],
                matchedPairs: {},
              }));

              if (this.isMatchingQuestion) {
                this.initializeMatchingQuestion();
              }

              // Initialize matching pairs after questions are loaded
              if (this.isCurrentQuestionMatching) {
                this.initializeMatchingPairs();
              }
              this.refreshCachedAnswers();
            }
            this.isLoadingQuestions = false;
          },
          error: (_) => {
            this.isLoadingQuestions = false;
          },
        });
    } else {
      this.questions = this.quizQuestions;

      this.userAnswers = this.questions.map((_, index) => ({
        questionIndex: index,
        selectedAnswers: [],
        matchedPairs: {},
      }));

      if (this.isMatchingQuestion) {
        this.initializeMatchingQuestion();
      }

      // Initialize matching pairs for non-async path
      if (this.isCurrentQuestionMatching) {
        this.initializeMatchingPairs();
      }
      this.refreshCachedAnswers();
    }
  }

  private refreshCachedAnswers(): void {
    const index = this.currentQuestionIndex;
    if (!this._shuffledAnswersCache.has(index)) {
      this._shuffledAnswersCache.set(
        index,
        this.shuffleAnswers(this.parsedAnswers)
      );
    }
    this.cachedAnswers = this._shuffledAnswersCache.get(index)!;
  }

  private shuffleAnswers(answers: ParsedAnswer[]): ParsedAnswer[] {
    const shuffled = [...answers];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  resetQuiz(): void {
    this.currentQuestionIndex = 0;
    this.showFeedback = false;
    this.currentFeedback = null;
    this.captchaResponse = null;
    this.draggedTerm = null;
    this.isDraggingFromDefinition = false;
    this.matchingTerms = [];
    this.matchingDefinitions = [];
    this.focusedTermIndex = 0;
    this.focusedDefinitionIndex = 0;
    this.termsListFocused = false;
    this.definitionsListFocused = false;
    this.matchingAnnouncement = '';
    this._shuffledAnswersCache.clear();
    this.userAnswers = this.questions.map((_, index) => ({
      questionIndex: index,
      selectedAnswers: [],
      matchedPairs: {},
    }));

    if (this.isMatchingQuestion) {
      this.initializeMatchingQuestion();
    }
  }

  get currentQuestion(): ApiQuizQuestion | undefined {
    return this.quizQuestions[this.currentQuestionIndex];
  }

  get currentQuestionType(): string {
    return this.currentQuestion?.questionType || '';
  }

  get isCurrentQuestionMatching(): boolean {
    return this.currentQuestionType.toLowerCase() === 'matching';
  }

  get parsedAnswers(): ParsedAnswer[] {
    if (!this.currentQuestion) return [];

    // Handle API response format (answers_terms is an array of strings)
    const answersArray =
      this.currentQuestion.answers_terms || this.currentQuestion.answers || [];

    return answersArray.map((answer: any, _: number) => {
      // If answer is already a string (from API), use it directly
      if (typeof answer === 'string') {
        return {
          answerText: answer,
          markdown: answer,
        };
      }

      // Otherwise, parse the markdown format (legacy)
      const lines = answer.markdown
        .split('\n')
        .filter((line: string) => line.trim());
      const result: ParsedAnswer = { answerText: '' };

      lines.forEach((line: string) => {
        if (line.includes('answerText')) {
          const match = line.match(/"answerText":\s*"([^"]+)"/);
          if (match) result.answerText = match[1];
        } else if (line.includes('matchingKey')) {
          const match = line.match(/"matchingKey":\s*"([^"]+)"/);
          if (match) result.matchingKey = match[1];
        } else if (
          !line.includes('{') &&
          !line.includes('}') &&
          !result.answerText
        ) {
          result.answerText = line.trim();
        }
      });

      return result;
    });
  }

  get isMultipleChoice(): boolean {
    const questionType = this.currentQuestionType.toLowerCase();
    return questionType === 'select-all-that-apply';
  }

  get isTrueFalse(): boolean {
    return (
      this.currentQuestion?.questionType === 'True/False' ||
      (this.parsedAnswers.length === 2 &&
        this.parsedAnswers.some((a) => a.answerText.toLowerCase() === 'true') &&
        this.parsedAnswers.some((a) => a.answerText.toLowerCase() === 'false'))
    );
  }

  get isMatchingQuestion(): boolean {
    return this.currentQuestion?.questionType?.toLowerCase() === 'matching';
  }

  get currentUserAnswer(): UserAnswer | undefined {
    return this.userAnswers[this.currentQuestionIndex];
  }

  get progressPercentage(): number {
    return ((this.currentQuestionIndex + 1) / this.quizQuestions.length) * 100;
  }

  initializeMatchingPairs(): void {
    if (!this.currentQuestion) return;

    const terms = this.currentQuestion.answers_terms || [];
    const definitions = this.currentQuestion.definitions || [];

    this.matchingPairs = terms.map((term, index) => ({
      term,
      definition: definitions[index] || '',
      selectedDefinitionIndex: null,
    }));

    this.availableDefinitions = definitions
      .map((text, index) => ({
        text,
        correctTermIndex: index,
        key: `def-${index}`,
      }))
      .sort(() => Math.random() - 0.5);
  }

  onMatchingSelect(pairIndex: number, definition: MatchingDefinition): void {
    if (this.showFeedback || !this.currentUserAnswer) return;
    const defIndex = this.availableDefinitions.indexOf(definition);
    this.matchingPairs[pairIndex].selectedDefinitionIndex = defIndex;

    const selectedIndices = this.matchingPairs
      .map((pair, idx) => (pair.selectedDefinitionIndex !== null ? idx : -1))
      .filter((idx) => idx !== -1);

    this.currentUserAnswer.selectedAnswers = selectedIndices;
  }

  get isLastQuestion(): boolean {
    return this.currentQuestionIndex === this.quizQuestions.length - 1;
  }

  // The security check is shown once the last question's feedback is displayed
  get showSecurityCheck(): boolean {
    return this.showFeedback && this.isLastQuestion;
  }

  get isFinishDisabled(): boolean {
    return this.showSecurityCheck && !this.captchaResponse;
  }

  onCaptchaResolved(response: string | null): void {
    // ng-recaptcha emits null when the token expires
    this.captchaResponse = response;
  }

  get isCheckDisabled(): boolean {
    if (!this.currentUserAnswer) return true;

    if (this.isMatchingQuestion) {
      const matchedPairs = this.currentUserAnswer.matchedPairs || {};
      const matchedCount = Object.keys(matchedPairs).length;
      const totalTerms = this.matchingTerms.length;
      return matchedCount < totalTerms;
    }
    return !this.currentUserAnswer?.selectedAnswers?.length;
  }

  initializeMatchingQuestion(): void {
    if (!this.isMatchingQuestion || !this.currentQuestion) return;

    const terms = this.currentQuestion.answers_terms || [];
    const definitions = this.currentQuestion.definitions || [];

    this.matchingTerms = terms.map((text, index) => ({
      text,
      key: `A${index + 1}`,
      index,
    }));

    this.matchingDefinitions = definitions.map((text, index) => ({
      text,
      key: `B${index + 1}`,
      correctTermIndex: -1,
      matchedTermKey: undefined,
    }));
  }

  onDragStart(term: MatchingTerm, event: DragEvent): void {
    if (this.showFeedback || !this.currentUserAnswer) return;
    this.draggedTerm = term;
    this.isDraggingFromDefinition = false;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', term.key);
    }
  }

  onMatchedTermDragStart(
    definition: MatchingDefinition,
    event: DragEvent
  ): void {
    if (
      this.showFeedback ||
      !definition.matchedTermKey ||
      !this.currentUserAnswer
    )
      return;

    const term = this.matchingTerms.find(
      (t) => t.key === definition.matchedTermKey
    );
    if (!term) return;

    this.draggedTerm = term;
    this.isDraggingFromDefinition = true;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', term.key);
    }
    this.cdr.detectChanges();
  }

  onDropOnTermsList(event: DragEvent): void {
    if (
      this.showFeedback ||
      !this.isDraggingFromDefinition ||
      !this.currentUserAnswer
    )
      return;
    event.preventDefault();

    if (this.draggedTerm) {
      const matchedPairs = this.currentUserAnswer.matchedPairs || {};
      const termKey = this.draggedTerm.key;
      const definitionKey = matchedPairs[termKey];

      delete matchedPairs[termKey];

      const definition = this.matchingDefinitions.find(
        (d) => d.key === definitionKey
      );
      if (definition) {
        definition.matchedTermKey = undefined;
      }

      this.currentUserAnswer.matchedPairs = matchedPairs;
      this.draggedTerm = null;
      this.isDraggingFromDefinition = false;
      this.cdr.detectChanges();
    }
  }

  onDragOverTermsList(event: DragEvent): void {
    if (this.showFeedback || !this.isDraggingFromDefinition) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.showFeedback) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDrop(definition: MatchingDefinition, event: DragEvent): void {
    if (this.showFeedback || !this.currentUserAnswer) return;
    event.preventDefault();

    if (this.draggedTerm) {
      const matchedPairs = this.currentUserAnswer.matchedPairs || {};

      const existingTermKey = definition.matchedTermKey;

      if (existingTermKey && this.isDraggingFromDefinition) {
        const draggedTermKey = this.draggedTerm.key;
        const sourceDefinitionKey = matchedPairs[draggedTermKey];
        const targetDefinitionKey = definition.key;
        matchedPairs[draggedTermKey] = targetDefinitionKey;
        matchedPairs[existingTermKey] = sourceDefinitionKey;

        definition.matchedTermKey = draggedTermKey;
        const sourceDefinition = this.matchingDefinitions.find(
          (d) => d.key === sourceDefinitionKey
        );
        if (sourceDefinition) {
          sourceDefinition.matchedTermKey = existingTermKey;
        }
      } else {
        Object.keys(matchedPairs).forEach((key) => {
          if (matchedPairs[key] === definition.key) {
            delete matchedPairs[key];
            this.matchingDefinitions.forEach((d) => {
              if (d.matchedTermKey === key) {
                d.matchedTermKey = undefined;
              }
            });
          }
        });

        if (
          this.isDraggingFromDefinition &&
          matchedPairs[this.draggedTerm.key]
        ) {
          const oldDefinitionKey = matchedPairs[this.draggedTerm.key];
          const oldDefinition = this.matchingDefinitions.find(
            (d) => d.key === oldDefinitionKey
          );
          if (oldDefinition) {
            oldDefinition.matchedTermKey = undefined;
          }
          delete matchedPairs[this.draggedTerm.key];
        }

        matchedPairs[this.draggedTerm.key] = definition.key;
        definition.matchedTermKey = this.draggedTerm.key;
      }

      this.currentUserAnswer.matchedPairs = matchedPairs;
      this.draggedTerm = null;
      this.isDraggingFromDefinition = false;
      this.cdr.detectChanges();
    }
  }

  onDragEnd(): void {
    this.draggedTerm = null;
    this.isDraggingFromDefinition = false;
  }

  onTermClick(term: MatchingTerm): void {
    if (this.showFeedback || !this.currentUserAnswer) return;

    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    const isTermCurrentlyMatched = this.isTermMatched(term);

    if (isTermCurrentlyMatched) {
      if (this.selectedTermForClick) {
        const sourceDefinitionKey = matchedPairs[this.selectedTermForClick.key];
        if (sourceDefinitionKey) {
          const definition = this.matchingDefinitions.find(
            (d) => d.key === sourceDefinitionKey
          );

          if (definition) {
            delete matchedPairs[this.selectedTermForClick.key];
            definition.matchedTermKey = undefined;
            this.currentUserAnswer.matchedPairs = matchedPairs;
            this.selectedTermForClick = null;
            this.cdr.detectChanges();
          }
          return;
        }
      }

      const definitionKey = matchedPairs[term.key];
      if (definitionKey) {
        const definition = this.matchingDefinitions.find(
          (d) => d.key === definitionKey
        );
        if (definition) {
          delete matchedPairs[term.key];
          definition.matchedTermKey = undefined;
          this.currentUserAnswer.matchedPairs = matchedPairs;
          this.ensureValidTermFocus();
          this.ensureValidDefinitionFocus();
          this.cdr.detectChanges();
        }
      }
      return;
    }

    if (!this.selectedTermForClick) {
      this.selectedTermForClick = term;
      this.cdr.detectChanges();
      return;
    }
    if (this.selectedTermForClick.key === term.key) {
      this.selectedTermForClick = null;
      this.cdr.detectChanges();
      return;
    }

    this.selectedTermForClick = term;
    this.cdr.detectChanges();
  }

  onMatchedTermClick(definition: MatchingDefinition, event?: Event): void {
    if (this.showFeedback || !definition.matchedTermKey) return;

    if (event) {
      event.stopPropagation();
    }

    const term = this.matchingTerms.find(
      (t) => t.key === definition.matchedTermKey
    );
    if (!term) return;

    if (this.selectedTermForClick?.key === term.key) {
      this.selectedTermForClick = null;
    } else {
      this.selectedTermForClick = term;
    }
    this.cdr.detectChanges();
  }
  onDefinitionClick(definition: MatchingDefinition): void {
    if (this.showFeedback || !this.currentUserAnswer) return;

    if (!this.selectedTermForClick && !definition.matchedTermKey) {
      return;
    }

    if (!this.selectedTermForClick && definition.matchedTermKey) {
      const term = this.matchingTerms.find(
        (t) => t.key === definition.matchedTermKey
      );
      if (term) {
        this.selectedTermForClick = term;
        this.cdr.detectChanges();
      }
      return;
    }

    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    const selectedTermKey = this.selectedTermForClick.key;
    const targetDefinitionKey = definition.key;
    const targetTermKey = definition.matchedTermKey;
    const sourceDefinitionKey = matchedPairs[selectedTermKey];

    if (sourceDefinitionKey === targetDefinitionKey) {
      this.selectedTermForClick = null;
      this.cdr.detectChanges();
      return;
    }

    if (!targetTermKey) {
      if (sourceDefinitionKey) {
        const sourceDefinition = this.matchingDefinitions.find(
          (d) => d.key === sourceDefinitionKey
        );
        if (sourceDefinition) {
          sourceDefinition.matchedTermKey = undefined;
        }
      }

      matchedPairs[selectedTermKey] = targetDefinitionKey;
      definition.matchedTermKey = selectedTermKey;
    } else if (targetTermKey && sourceDefinitionKey) {
      matchedPairs[selectedTermKey] = targetDefinitionKey;
      matchedPairs[targetTermKey] = sourceDefinitionKey;

      definition.matchedTermKey = selectedTermKey;
      const sourceDefinition = this.matchingDefinitions.find(
        (d) => d.key === sourceDefinitionKey
      );
      if (sourceDefinition) {
        sourceDefinition.matchedTermKey = targetTermKey;
      }
    } else if (targetTermKey && !sourceDefinitionKey) {
      delete matchedPairs[targetTermKey];
      matchedPairs[selectedTermKey] = targetDefinitionKey;
      definition.matchedTermKey = selectedTermKey;
    }

    this.currentUserAnswer.matchedPairs = matchedPairs;
    this.selectedTermForClick = null;
    this.ensureValidTermFocus();
    this.ensureValidDefinitionFocus();
    this.cdr.detectChanges();
  }

  isTermSelected(term: MatchingTerm): boolean {
    if (!this.selectedTermForClick || !this.currentUserAnswer) return false;

    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    const selectedTermKey = this.selectedTermForClick.key;
    const sourceDefinitionKey = matchedPairs[selectedTermKey];

    if (sourceDefinitionKey) {
      return false;
    }

    return this.selectedTermForClick.key === term.key;
  }

  removeMatchFromDefinition(
    definition: MatchingDefinition,
    event?: Event
  ): void {
    if (this.showFeedback || !this.currentUserAnswer) return;

    if (event) {
      event.stopPropagation();
    }

    const matchedPairs = this.currentUserAnswer.matchedPairs || {};

    Object.keys(matchedPairs).forEach((key) => {
      if (matchedPairs[key] === definition.key) {
        delete matchedPairs[key];
      }
    });

    definition.matchedTermKey = undefined;
    this.currentUserAnswer.matchedPairs = matchedPairs;
    this.cdr.detectChanges();
  }

  getMatchedTermText(definition: MatchingDefinition): string {
    if (!definition.matchedTermKey) return '';
    const term = this.matchingTerms.find(
      (t) => t.key === definition.matchedTermKey
    );
    return term ? term.text : '';
  }

  /** True when the term has been paired with a definition (independent of showFeedback). */
  isTermPaired(term: MatchingTerm): boolean {
    if (!this.currentUserAnswer) return false;
    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    return matchedPairs[term.key] !== undefined;
  }

  /** True when the definition already has a term matched to it. */
  isDefinitionPaired(definition: MatchingDefinition): boolean {
    return !!definition.matchedTermKey;
  }

  getTermAriaLabel(term: MatchingTerm, index: number): string {
    let label =
      index === 0
        ? `${
            this.currentQuestion?.questionText || ''
          } ${this._translate.instant('studentLearningQuiz.terms_heading')} ${
            term.text
          }`
        : term.text;
    if (this.isTermPaired(term)) {
      label += `, ${this._translate.instant(
        'studentLearningQuiz.already_selected'
      )}`;
    } else if (this.selectedTermForClick?.key === term.key) {
      label += `, ${this._translate.instant(
        'studentLearningQuiz.term_selected_state'
      )}`;
    }
    return label;
  }

  getDefinitionAriaLabel(definition: MatchingDefinition): string {
    let label = definition.text;
    if (this.isDefinitionPaired(definition)) {
      label += `, ${this._translate.instant(
        'studentLearningQuiz.already_selected'
      )}`;
    }
    return label;
  }

  private ensureValidTermFocus(): void {
    const total = this.matchingTerms.length;
    if (total === 0) return;
    if (this.focusedTermIndex >= total) {
      this.focusedTermIndex = 0;
    }
    let tries = 0;
    while (
      tries < total &&
      this.isTermPaired(this.matchingTerms[this.focusedTermIndex])
    ) {
      this.focusedTermIndex = (this.focusedTermIndex + 1) % total;
      tries++;
    }
  }

  private ensureValidDefinitionFocus(): void {
    const total = this.matchingDefinitions.length;
    if (total === 0) return;
    if (this.focusedDefinitionIndex >= total) {
      this.focusedDefinitionIndex = 0;
    }
  }

  isTermMatched(term: MatchingTerm): boolean {
    if (this.showFeedback || !this.currentUserAnswer) return true;
    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    return matchedPairs[term.key] !== undefined;
  }

  isDefinitionCorrect(definition: MatchingDefinition): boolean {
    if (
      !this.showFeedback ||
      !definition.matchedTermKey ||
      !this.currentQuizAnswerResponse
    )
      return false;

    // The 'correct' array in the response is ordered the same way as
    // matchingTerms was when selectedAnswers was built in checkAnswer(),
    // so find the matched term's position in that same array.
    const termIndex = this.matchingTerms.findIndex(
      (t) => t.key === definition.matchedTermKey
    );
    if (termIndex === -1) return false;

    return this.currentQuizAnswerResponse.correct[termIndex] === true;
  }

  isDefinitionIncorrect(definition: MatchingDefinition): boolean {
    if (!this.showFeedback || !definition.matchedTermKey) return false;
    return !this.isDefinitionCorrect(definition);
  }

  isDefinitionHighlighted(definition: MatchingDefinition): boolean {
    if (!this.selectedTermForClick || !this.currentUserAnswer) return false;
    const selectedTermKey = this.selectedTermForClick.key;
    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    const sourceDefinitionKey = matchedPairs[selectedTermKey];

    if (sourceDefinitionKey === definition.key) return false;

    if (sourceDefinitionKey) {
      return true;
    }

    return !definition.matchedTermKey;
  }
  isTermsListHighlighted(): boolean {
    if (
      !this.selectedTermForClick ||
      this.showFeedback ||
      !this.currentUserAnswer
    ) {
      return false;
    }

    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    const selectedTermKey = this.selectedTermForClick.key;
    const sourceDefinitionKey = matchedPairs[selectedTermKey];

    return !!sourceDefinitionKey;
  }

  onTermsListClick(event: Event): void {
    if (
      this.showFeedback ||
      !this.selectedTermForClick ||
      !this.currentUserAnswer
    ) {
      return;
    }

    const target = event.target as HTMLElement;
    const isTermChip =
      target.classList.contains('term-chip') || target.closest('.term-chip');

    if (isTermChip) {
      return;
    }

    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    const selectedTermKey = this.selectedTermForClick.key;
    const sourceDefinitionKey = matchedPairs[selectedTermKey];

    if (sourceDefinitionKey) {
      const definition = this.matchingDefinitions.find(
        (d) => d.key === sourceDefinitionKey
      );

      if (definition) {
        delete matchedPairs[selectedTermKey];
        definition.matchedTermKey = undefined;
        this.currentUserAnswer.matchedPairs = matchedPairs;
        this.selectedTermForClick = null;
        this.cdr.detectChanges();
      }
    }
  }
  isTermAvailableForSelection(term: MatchingTerm): boolean {
    if (this.showFeedback || !this.currentUserAnswer) return false;

    const matchedPairs = this.currentUserAnswer.matchedPairs || {};
    const selectedTermKey = this.selectedTermForClick?.key;
    if (!selectedTermKey) {
      return !this.isTermMatched(term);
    }
    const sourceDefinitionKey = matchedPairs[selectedTermKey];

    if (sourceDefinitionKey) {
      return false;
    }

    return false;
  }

  onRadioChange(answerIndex: number): void {
    if (this.showFeedback || !this.currentUserAnswer) return;
    this.currentUserAnswer.selectedAnswers = [answerIndex];
  }

  onCheckboxChange(answerIndex: number): void {
    if (this.showFeedback || !this.currentUserAnswer) return;

    const index = this.currentUserAnswer.selectedAnswers.indexOf(answerIndex);
    if (index > -1) {
      this.currentUserAnswer.selectedAnswers.splice(index, 1);
    } else {
      this.currentUserAnswer.selectedAnswers.push(answerIndex);
    }
  }

  selectAnswer(answerIndex: number): void {
    if (this.showFeedback) return;

    if (this.isMultipleChoice) {
      this.onCheckboxChange(answerIndex);
    } else {
      this.onRadioChange(answerIndex);
    }
  }

  trackByTermKey(_index: number, term: MatchingTerm): string {
    return term.key;
  }

  trackByDefinitionKey(_index: number, definition: MatchingDefinition): string {
    return definition.key;
  }

  private announceMatchingState(message: string): void {
    clearTimeout(this._matchingAnnouncementTimer);
    this.matchingAnnouncement = '';
    this.cdr.detectChanges();
    this._matchingAnnouncementTimer = setTimeout(() => {
      this.matchingAnnouncement = message;
      this.cdr.detectChanges();
    }, 50);
  }

  onTermKeydown(event: KeyboardEvent, index: number): void {
    const total = this.matchingTerms.length;
    if (total === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusedTermIndex = (index + 1) % total;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusedTermIndex = (index - 1 + total) % total;
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.showFeedback) {
        const term = this.matchingTerms[index];
        const isPaired = this.isTermPaired(term);
        const wasSelected = this.selectedTermForClick?.key === term.key;
        this.onTermClick(term);
        let msg: string;
        if (isPaired) {
          msg = this._translate.instant(
            'studentLearningQuiz.term_unmatched_announcement',
            { term: term.text }
          );
        } else {
          msg = wasSelected
            ? this._translate.instant(
                'studentLearningQuiz.term_deselected_announcement',
                { term: term.text }
              )
            : this._translate.instant(
                'studentLearningQuiz.term_selected_announcement',
                { term: term.text }
              );
        }
        this.announceMatchingState(msg);
      }
    }
  }

  onDefinitionKeydown(event: KeyboardEvent, index: number): void {
    const total = this.matchingDefinitions.length;
    if (total === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusedDefinitionIndex = (index + 1) % total;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusedDefinitionIndex = (index - 1 + total) % total;
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.showFeedback) {
        const definition = this.matchingDefinitions[index];
        const pendingTerm = this.selectedTermForClick;
        const isPickUp = !pendingTerm && !!definition.matchedTermKey;
        const pickedUpTermText = isPickUp
          ? this.getMatchedTermText(definition)
          : '';
        this.onDefinitionClick(definition);
        if (isPickUp) {
          // Term was picked up from this definition — announce it
          this.announceMatchingState(
            this._translate.instant(
              'studentLearningQuiz.term_picked_up_announcement',
              { term: pickedUpTermText }
            )
          );
        } else if (
          pendingTerm &&
          this.selectedTermForClick === null &&
          definition.matchedTermKey
        ) {
          // Term was placed onto this definition — announce it
          const pairedTermText = this.getMatchedTermText(definition);
          this.announceMatchingState(
            `${definition.text}, ${pairedTermText} ${this._translate.instant(
              'studentLearningQuiz.selected_label'
            )}`
          );
        }
      }
    }
  }

  get activeTermId(): string | null {
    const term = this.matchingTerms[this.focusedTermIndex];
    return term ? 'term-opt-' + term.key : null;
  }

  get activeDefinitionId(): string | null {
    const def = this.matchingDefinitions[this.focusedDefinitionIndex];
    return def ? 'def-opt-' + def.key : null;
  }

  isSelected(answerIndex: number): boolean {
    return (
      this.currentUserAnswer?.selectedAnswers?.includes(answerIndex) || false
    );
  }

  checkAnswer(): void {
    if (
      this.isCheckDisabled ||
      !this.currentUserAnswer ||
      !this.currentQuestion
    )
      return;

    this.isLoadingQuizAnswer = true;
    let selectedAnswers: string[];
    if (this.isMatchingQuestion) {
      const matchedPairsForSubmit = this.currentUserAnswer.matchedPairs || {};
      selectedAnswers = this.matchingTerms.map((term) => {
        const matchedDef = this.matchingDefinitions.find(
          (d) => d.key === matchedPairsForSubmit[term.key]
        );
        return `${term.text} | ${matchedDef?.text || ''}`;
      });
    } else {
      selectedAnswers = this.currentUserAnswer.selectedAnswers
        .map((index) => this.cachedAnswers[index]?.answerText)
        .filter((text): text is string => !!text);
    }

    const quizAnswer = {
      questionPath: this.currentQuestion._path,
      selectedAnswers,
    };
    this._apiService
      .getQuizAnswer(quizAnswer)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: (response: ApiGetQuizAnswer) => {
          this.currentQuizAnswerResponse = response;
          this.currentUserAnswer.isCorrect =
            this.isQuestionAnsweredCorrectly(response);
          this.showFeedback = true;
          this.currentFeedback = {
            correct: this.currentUserAnswer.isCorrect,
            explanation: response?.explanation?.html || '',
          };
          this.isLoadingQuizAnswer = false;
          this.continueAriaLabel = this._buildContinueAriaLabel();
          this.cdr.detectChanges();
          this._checkFocusTimer = setTimeout(() =>
            this.continueBtnRef?.nativeElement?.querySelector('button')?.focus()
          );
        },
        error: () => {
          this.isLoadingQuizAnswer = false;
        },
      });
  }

  isQuestionAnsweredCorrectly(response: ApiGetQuizAnswer): boolean {
    const numberOfCorrectAnswersSelected =
      response?.correct.filter((isCorrect) => isCorrect).length || 0;
    const numberOfIncorrectAnswersSelected =
      response?.correct.filter((isCorrect) => !isCorrect).length || 0;
    return (
      numberOfCorrectAnswersSelected === response?.numberOfCorrectAnswers &&
      numberOfIncorrectAnswersSelected === 0
    );
  }

  continue(): void {
    if (this.isFinishDisabled) return;

    this.showFeedback = false;
    this.currentFeedback = null;
    this.continueAriaLabel = '';
    this.selectedTermForClick = null;
    this.focusedTermIndex = 0;
    this.focusedDefinitionIndex = 0;
    this.termsListFocused = false;
    this.definitionsListFocused = false;

    if (this.currentQuestionIndex < this.quizQuestions.length - 1) {
      this.currentQuestionIndex++;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.refreshCachedAnswers();

      const nextAnswer = this.userAnswers[this.currentQuestionIndex];
      const alreadyChecked = nextAnswer && nextAnswer.isCorrect !== undefined;

      if (alreadyChecked) {
        this.showFeedback = true;

        if (this.isMatchingQuestion) {
          this.initializeMatchingQuestion();
          if (nextAnswer.matchedPairs) {
            this.restoreMatchedPairs(nextAnswer.matchedPairs);
          }
        }

        this.currentFeedback = {
          correct: nextAnswer.isCorrect || false,
          explanation: nextAnswer.isCorrect
            ? ''
            : this.currentQuestion?.explanation?.html || '',
        };
        this.continueAriaLabel = this._buildContinueAriaLabel();
      } else {
        if (nextAnswer) {
          nextAnswer.selectedAnswers = [];
          nextAnswer.matchedPairs = {};
        }
        if (this.isMatchingQuestion) {
          this.initializeMatchingQuestion();
        }
      }
      this.cdr.detectChanges();
      this.backToLibraryBtn?.nativeElement?.focus();
    } else {
      this.submitQuizResults();
    }
  }

  getContinueAriaLabel(): string {
    return this.continueAriaLabel;
  }

  private _buildContinueAriaLabel(): string {
    if (!this.currentFeedback) return '';

    let feedbackLabel: string;
    if (this.isMatchingQuestion) {
      if (this.isMatchingAllCorrect) {
        feedbackLabel = this._translate.instant(
          'studentLearningQuiz.correct_feedback'
        );
      } else if (this.isMatchingPartiallyCorrect) {
        feedbackLabel = `${this._translate.instant(
          'studentLearningQuiz.partial_correct_feedback'
        )} ${this.matchingScoreText} ${this._translate.instant(
          'studentLearningQuiz.correct_text'
        )}`;
      } else {
        feedbackLabel = this._translate.instant(
          'studentLearningQuiz.incorrect_feedback'
        );
      }
    } else {
      feedbackLabel = this._translate.instant(
        this.currentFeedback.correct
          ? 'studentLearningQuiz.correct_feedback'
          : 'studentLearningQuiz.incorrect_feedback'
      );
    }

    if (this.currentFeedback.explanation) {
      const tmp = document.createElement('div');
      tmp.innerHTML = this.currentFeedback.explanation;
      const plainText = (tmp.textContent || tmp.innerText || '').trim();
      if (plainText) {
        const whyLabel = this._translate.instant(
          'studentLearningQuiz.why_text'
        );
        feedbackLabel += ` ${whyLabel} ${plainText}`;
      }
    }

    const actionLabel =
      this.currentQuestionIndex < this.quizQuestions.length - 1
        ? this._translate.instant('studentLearningQuiz.continue_button')
        : this._translate.instant('studentLearningQuiz.finish_quiz_button');

    return `${feedbackLabel} ${actionLabel}`;
  }

  goToPrevious(): void {
    // The reCAPTCHA widget is destroyed when leaving the last question
    this.captchaResponse = null;

    if (this.currentQuestionIndex > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.currentQuestionIndex--;
      this.refreshCachedAnswers();
      this.selectedTermForClick = null;
      this.focusedTermIndex = 0;
      this.focusedDefinitionIndex = 0;
      this.termsListFocused = false;
      this.definitionsListFocused = false;

      const previousAnswer = this.userAnswers[this.currentQuestionIndex];
      const alreadyChecked =
        previousAnswer && previousAnswer.isCorrect !== undefined;

      if (alreadyChecked) {
        this.showFeedback = true;

        if (this.isMatchingQuestion) {
          this.initializeMatchingQuestion();
          if (previousAnswer.matchedPairs) {
            this.restoreMatchedPairs(previousAnswer.matchedPairs);
          }
        }

        this.currentFeedback = {
          correct: previousAnswer.isCorrect || false,
          explanation: previousAnswer.isCorrect
            ? ''
            : this.currentQuestion?.explanation?.html || '',
        };
        this.continueAriaLabel = this._buildContinueAriaLabel();
      } else {
        if (previousAnswer) {
          previousAnswer.selectedAnswers = [];
          previousAnswer.matchedPairs = {};
        }
        this.showFeedback = false;
        this.currentFeedback = null;
        this.continueAriaLabel = '';
        if (this.isMatchingQuestion) {
          this.initializeMatchingQuestion();
        }
      }
    } else {
      this.backToQuizIntro.emit();
    }
  }

  private restoreMatchedPairs(matchedPairs: Record<string, string>): void {
    Object.entries(matchedPairs).forEach(([termKey, defKey]) => {
      const definition = this.matchingDefinitions.find((d) => d.key === defKey);
      if (definition) {
        definition.matchedTermKey = termKey;
      }
    });
  }

  goToLibrary(): void {
    this.backWarningDialog?.openModalDialog();
  }

  confirmGoToLibrary(): void {
    this.resetQuiz();
    this.backToLibrary.emit();
  }

  submitQuizResults(): void {
    const totalQuestions = this.quizQuestions.length;
    const correctAnswers = this.userAnswers.filter((a) => a.isCorrect).length;

    // Guard against division by zero
    let score = 0;
    if (totalQuestions > 0) {
      score = Math.round((correctAnswers / totalQuestions) * 100);
    }

    const quizStatus = score >= 70 ? 'passed' : 'failed'; // Assuming 70% is passing

    const results = {
      totalQuestions,
      correctAnswers,
      score,
      quizStatus,
      userAnswers: this.userAnswers,
      quizData: this.quizData,
    };

    // Always continue to the completion screen, even if creating the school or saving the score fails
    this.isSavingScore = true;
    this._resolveSchoolMetaDataId()
      .pipe(
        switchMap((schoolMetaDataId) =>
          this._apiService.savePublicLearningLibraryScore(
            this._buildScoreRequest(score, schoolMetaDataId)
          )
        ),
        takeUntil(this._destroyed$),
        finalize(() => {
          this.isSavingScore = false;
          this.finishQuiz(results);
        })
      )
      .subscribe({
        error: (_) => {},
      });
  }

  private _resolveSchoolMetaDataId(): Observable<string | undefined> {
    const newSchool = this.quizFormValue?.newSchool;
    if (!newSchool) {
      return of(this.quizFormValue?.schoolMetaDataId);
    }
    return this._apiService.createSchoolMetaData([newSchool]).pipe(
      map((response) => {
        const schoolMetaDataId =
          !response?.error &&
          !response?.code &&
          response?.[0]?.schoolMetaDataId;
        if (!schoolMetaDataId) {
          throw new Error('Failed to create school');
        }
        return schoolMetaDataId;
      })
    );
  }

  private _buildScoreRequest(score: number, schoolMetaDataId?: string) {
    const { role, gradeLevel, howDidYouKnowAboutUs } =
      this.quizFormValue || {};
    return {
      ...(schoolMetaDataId && { schoolMetaDataId }),
      role,
      ...(gradeLevel && { gradeLevel }),
      howDidYouKnowAboutUsCrmId: howDidYouKnowAboutUs,
      lessonPath: this.lessonPath,
      score,
      captchaResponse: this.captchaResponse,
    };
  }

  private finishQuiz(results: any): void {
    // Emit results to parent component to show the completion screen
    this.submitQuiz.emit(results);

    this.currentQuestionIndex = 0;
    this.showFeedback = false;
    this.currentFeedback = null;
    this.captchaResponse = null;
    this.draggedTerm = null;
    this.isDraggingFromDefinition = false;
    this.matchingTerms = [];
    this.matchingDefinitions = [];
    this._shuffledAnswersCache.clear();
    this.userAnswers = this.questions.map((_, index) => ({
      questionIndex: index,
      selectedAnswers: [],
      matchedPairs: {},
    }));
  }

  trackByAnswerIndex(index: number): string {
    return `${this.currentQuestionIndex}_${index}`;
  }

  isAnswerCorrect(answerIndex: number): boolean {
    if (
      !this.showFeedback ||
      !this.currentUserAnswer ||
      !this.currentQuizAnswerResponse
    )
      return false;

    // The 'correct' array in the response is ordered the same way as
    // currentUserAnswer.selectedAnswers was when selectedAnswers was built
    // in checkAnswer(), so find the answer's position in that same array.
    const selectedIndex =
      this.currentUserAnswer.selectedAnswers.indexOf(answerIndex);
    if (selectedIndex === -1) return false;

    return this.currentQuizAnswerResponse.correct[selectedIndex] === true;
  }

  isAnswerIncorrect(answerIndex: number): boolean {
    if (!this.showFeedback) return false;
    return this.isSelected(answerIndex) && !this.isAnswerCorrect(answerIndex);
  }

  getMatchingScore(): { correct: number; total: number } {
    if (
      !this.showFeedback ||
      !this.isMatchingQuestion ||
      !this.currentUserAnswer ||
      !this.currentQuestion
    ) {
      return { correct: 0, total: 0 };
    }

    const correct =
      this.currentQuizAnswerResponse?.correct.filter((isCorrect) => isCorrect)
        .length || 0;
    const total = this.currentQuizAnswerResponse?.numberOfCorrectAnswers || 0;

    return { correct, total };
  }

  get matchingScoreText(): string {
    const score = this.getMatchingScore();
    return `${score.correct}/${score.total}`;
  }

  get matchedPairs() {
    return this.currentUserAnswer?.matchedPairs || {};
  }

  get isMatchingPartiallyCorrect(): boolean {
    const score = this.getMatchingScore();
    return score.correct > 0 && score.correct < score.total;
  }

  get isMatchingAllCorrect(): boolean {
    const score = this.getMatchingScore();
    return score.correct === score.total && score.total > 0;
  }

  get isMatchingAllIncorrect(): boolean {
    const score = this.getMatchingScore();
    return score.correct === 0 && score.total > 0;
  }
}
